import { atLeast, type Role } from "@/lib/types";

/**
 * Product tours (ACL-332): a screen explains itself one element at a time.
 *
 * A tour is a list of steps written beside its screen (`const CAMPAIGNS_TOUR` at the top of
 * `routes/campaigns.tsx`), and each step names an element by its `data-tour` attribute. The screen
 * calls `useTour(tour, { ready })` from `components/layout/tour.tsx`, which starts the tour on its
 * own the first time the person opens the screen and draws "Take the tour" in the page header for
 * every time after. This file holds the rules, pure, so they are tested rather than re-derived at
 * a call site:
 *
 *   - which steps a role gets (`tourSteps`): a supervisor who can only read a page is not shown
 *     the steps for the admin's controls;
 *   - whether a tour starts on its own (`shouldOfferTour`): once per person per version, never
 *     while a call rings or is up, and never while an admin is signed in as somebody else. A
 *     dialog that is open when the screen opens (the phone's audio setup, at sign-in) is waited
 *     for, not skipped (`modalOpen`);
 *   - what is remembered (`seenTour`, `tourPrefKey`): the version taken and whether it was finished
 *     or skipped, on the account (`ui_prefs`) and not in the browser, so a tour taken on one
 *     computer stays taken on the next. One top-level key per tour, because `PUT /auth/me/ui-prefs`
 *     merges top-level keys and nothing deeper: two panes finishing two tours cannot lose each
 *     other's record;
 *   - which element a step points at (`findTarget`: the one that is drawn), whether the page has
 *     to scroll to it (`needsScroll`), and which side of it the words go (`placeWords`).
 */

export type TourSide = "top" | "right" | "bottom" | "left";

export interface TourStep {
  /** The element, by the value of its `data-tour` attribute. A step whose element is not on the
   *  page is left out of the tour (or skipped, when it only appears after `search` is applied). */
  target: string;
  title: string;
  body: string;
  /** The lowest role that can use the element. A step everyone can read has none. */
  minimum?: Role;
  /** URL search parameters set before the element is looked for: the tab it sits on. An empty
   *  value removes the parameter. The tour puts the URL back as it found it when it ends. */
  search?: Record<string, string>;
  /** Which side of the element the words go, when there is room there (`placeWords`). */
  side?: TourSide;
  align?: "start" | "center" | "end";
}

export interface Tour {
  /** Stable: the person's record is kept under `tour_<id>` in their `ui_prefs`. */
  id: string;
  /** Raise it when the screen changed enough that someone who took the tour should take it again.
   *  Everyone who took a lower version gets the tour once more. */
  version: number;
  steps: TourStep[];
}

export type TourOutcome = "finished" | "skipped";

/** What `ui_prefs` keeps per tour. */
export interface TourSeen {
  version: number;
  done: TourOutcome;
  /** ISO instant. */
  at: string;
}

/** The longest a step waits for its element after `search` is applied, before it is skipped. */
export const TOUR_WAIT_MS = 2500;

/** The margin, in CSS pixels, an element keeps from the viewport's edges before it is scrolled
 *  into view, so the words beside it have room. */
export const TOUR_SCROLL_MARGIN = 56;

export function tourPrefKey(id: string): string {
  return `tour_${id}`;
}

export function targetSelector(target: string): string {
  return `[data-tour="${target.replace(/["\\]/g, "\\$&")}"]`;
}

/**
 * The element a step points at: the first one carrying the name that is drawn. A screen may render
 * the same thing twice, for a wide and a narrow page, with one of the two hidden (the cadence
 * page's preview), and an element that is not drawn has no box to point at.
 */
export function findTarget(
  target: string, doc: Pick<Document, "querySelectorAll"> = document,
): Element | null {
  for (const element of Array.from(doc.querySelectorAll(targetSelector(target)))) {
    if (element.getClientRects().length > 0) return element;
  }
  return null;
}

/** The steps this role gets, in order. A role too low for every step gets none. */
export function tourSteps(tour: Tour, role: Role | undefined): TourStep[] {
  if (!role) return [];
  return tour.steps.filter((step) => !step.minimum || atLeast(role, step.minimum));
}

/** The person's record of a tour, or null for none or one that does not parse. */
export function seenTour(prefs: Record<string, unknown> | undefined, id: string): TourSeen | null {
  const raw = prefs?.[tourPrefKey(id)];
  if (!raw || typeof raw !== "object") return null;
  const { version, done, at } = raw as Record<string, unknown>;
  if (typeof version !== "number" || !Number.isFinite(version)) return null;
  if (done !== "finished" && done !== "skipped") return null;
  return { version, done, at: typeof at === "string" ? at : "" };
}

export function tourRecord(tour: Tour, done: TourOutcome, now = new Date()): TourSeen {
  return { version: tour.version, done, at: now.toISOString() };
}

export interface OfferInput {
  role: Role | undefined;
  /** The person's `ui_prefs`, or undefined while they have not loaded (or failed to): a tour that
   *  may already have been taken is not started on a guess. */
  prefs: Record<string, unknown> | undefined;
  /** A call ringing, being placed, up or on hold. */
  onCall: boolean;
  /** An admin signed in as this person with a sign-in link (ADR-040). */
  signedInAs: boolean;
}

/**
 * Whether the tour starts on its own when its screen opens. Once per version: a tour finished or
 * skipped at this version or a later one stays away. Not during a call, because a tour takes the
 * page and the rep's attention. Not while signed in as somebody else: the admin is looking at the
 * person's console, and a tour taken there would be recorded against the person.
 */
export function shouldOfferTour(tour: Tour, input: OfferInput): boolean {
  if (input.onCall || input.signedInAs || !input.prefs) return false;
  if (tourSteps(tour, input.role).length === 0) return false;
  const seen = seenTour(input.prefs, tour.id);
  return seen === null || seen.version < tour.version;
}

/**
 * Whether a dialog (or an open menu) has the page: Radix turns the body's pointer events off for
 * as long as a modal layer is open, and that is the one signal every modal in the console shares.
 * A tour that started then would draw its words over the dialog and fight its focus trap, so the
 * tour waits for the dialog to close and starts after.
 */
export function modalOpen(doc: Pick<Document, "body"> = document): boolean {
  return doc.body.style.pointerEvents === "none";
}

export interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Whether an element is too tall for the page to show whole, with the margin kept: a long list. */
export function tallerThanView(
  rect: Box, viewport: { height: number }, margin = TOUR_SCROLL_MARGIN,
): boolean {
  return rect.height > viewport.height - margin * 2;
}

/**
 * Whether an element has to be scrolled into view before it is pointed at: any part of it is
 * within `margin` of the viewport's edges, or outside them. One too tall to show whole is left
 * alone while its top is on screen in the upper half: scrolling a long list to its middle would
 * push its own heading off the page for nothing.
 */
export function needsScroll(
  rect: Box, viewport: { width: number; height: number }, margin = TOUR_SCROLL_MARGIN,
): boolean {
  if (rect.left < margin || rect.left + rect.width > viewport.width - margin) return true;
  if (tallerThanView(rect, viewport, margin)) return rect.top < margin || rect.top > viewport.height / 2;
  return rect.top < margin || rect.top + rect.height > viewport.height - margin;
}

/** The room the words take beside an element, with their gap to it and to the page's edge. */
export const TOUR_WORDS = { width: 312, height: 214 };

/**
 * Which side of its element a step's words go. The side the step asked for when there is room
 * for the words there, else the opposite side, else one of the other two: an element as wide as
 * the page has no left or right to offer on a narrow screen, and the popover's own collision
 * handling only ever flips to the opposite side. With no room on any side (a list as big as the
 * page) the words go inside the element, at its top.
 */
export function placeWords(
  preferred: TourSide, box: Box, page: Box, words = TOUR_WORDS,
): { side: TourSide; inside: boolean } {
  const room: Record<TourSide, number> = {
    top: box.top - page.top,
    bottom: page.top + page.height - (box.top + box.height),
    left: box.left - page.left,
    right: page.left + page.width - (box.left + box.width),
  };
  const fits = (side: TourSide) =>
    room[side] >= (side === "top" || side === "bottom" ? words.height : words.width);
  const opposite: Record<TourSide, TourSide> = { top: "bottom", bottom: "top", left: "right", right: "left" };
  const others: TourSide[] = preferred === "left" || preferred === "right" ? ["bottom", "top"] : ["right", "left"];
  for (const side of [preferred, opposite[preferred], ...others]) {
    if (fits(side)) return { side, inside: false };
  }
  return { side: "bottom", inside: true };
}

/** The URL's search with a step's parameters applied: a value sets, an empty value removes. */
export function withSearch(current: URLSearchParams, patch: Record<string, string>): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const [key, value] of Object.entries(patch)) {
    if (value === "") next.delete(key);
    else next.set(key, value);
  }
  return next;
}

/** Whether two boxes are the same to the pixel, so a measuring loop only re-renders on a move. */
export function sameBox(a: Box | null, b: Box | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    Math.round(a.top) === Math.round(b.top) &&
    Math.round(a.left) === Math.round(b.left) &&
    Math.round(a.width) === Math.round(b.width) &&
    Math.round(a.height) === Math.round(b.height)
  );
}

/** How often a waiting step looks again. An element can come into view with no change to the
 *  tree at all (a container that grows past a breakpoint), which no observer reports. */
const TOUR_POLL_MS = 150;

/**
 * The element a step points at, now or as soon as it is drawn (a tab's content renders after the
 * URL changes), or null when it has not come within `timeoutMs` or the wait is abandoned.
 */
export function waitForTarget(
  target: string, timeoutMs: number, signal?: AbortSignal,
): Promise<Element | null> {
  return new Promise((resolve) => {
    const now = findTarget(target);
    if (now) {
      resolve(now);
      return;
    }
    let settled = false;
    const finish = (element: Element | null) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      clearInterval(poll);
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
      resolve(element);
    };
    const look = () => {
      const found = findTarget(target);
      if (found) finish(found);
    };
    const observer = new MutationObserver(look);
    observer.observe(document.body, {
      childList: true, subtree: true, attributes: true, attributeFilter: ["data-tour"],
    });
    const poll = setInterval(look, TOUR_POLL_MS);
    const timer = setTimeout(() => finish(null), timeoutMs);
    const abort = () => finish(null);
    if (signal?.aborted) abort();
    else signal?.addEventListener("abort", abort);
  });
}
